import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import TroisWa from "../assets/logo_companies/3wa.jpg";
import Defense from "../assets/logo_companies/ministere_defense.webp";
import Passerelle from "../assets/logo_companies/passerelle.jpeg";
import Fonderie from "../assets/logo_companies/fonderie.png";
import ESD from "../assets/logo_companies/esd.png";
import Doranco from "../assets/logo_companies/doranco.jpg";
import Apecap from "../assets/logo_companies/apecap.png";
import GIP from "../assets/logo_companies/gip.webp";

/* Délais d'animation décalés pour chaque carte */
const DELAYS = [0, 0.6, 1.2, 0.3, 0.9, 1.5, 0.2, 1.1];
const DURATIONS = [4.2, 3.8, 4.5, 4.0, 3.6, 4.8, 4.1, 3.9];

const CompaniesSection = () => {
  const { t } = useTranslation();

  const organizations = [
    { name: t("services.companies.TroisWA"), image: TroisWa },
    { name: t("services.companies.defense"), image: Defense },
    { name: t("services.companies.campus"), image: Fonderie },
    { name: t("services.companies.passerelle"), image: Passerelle },
    { name: t("services.companies.esd"), image: ESD },
    { name: t("services.companies.doranco"), image: Doranco },
    { name: t("services.companies.apecap"), image: Apecap },
    { name: t("services.companies.gip"), image: GIP },
  ];

  return (
    <TestimonialsSection>
      <GlowEffect $pos="top-left" />
      <GlowEffect $pos="bottom-right" />

      <Title>{t("services.titleCompanies")}</Title>
      <Subtitle>{t("services.descCompanies")}</Subtitle>

      <LogoGrid>
        {organizations.map((org, index) => (
          <LogoCard
            key={index}
            $duration={DURATIONS[index]}
            $delay={DELAYS[index]}
          >
            <LogoImage src={org.image} alt={org.name} />
            <LogoText>{org.name}</LogoText>
          </LogoCard>
        ))}
      </LogoGrid>
    </TestimonialsSection>
  );
};

export default CompaniesSection;

/* ─── Animations ──────────────────────────────────────────────────────────── */

const floatAnim = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-14px); }
`;

/* ─── Styles ──────────────────────────────────────────────────────────────── */

export const TestimonialsSection = styled.section`
  padding: 80px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
`;

const Title = styled.h2`
  background: linear-gradient(to right, #ffffff, rgba(180, 1, 18, 1));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-transform: uppercase;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  color: #b8b8d1;
  text-align: center;
  max-width: 700px;
  line-height: 1.6;
  margin-bottom: 3rem;
`;

/* Grille fluide — 4 colonnes desktop, 3 tablette, 2 mobile */
const LogoGrid = styled.div`
  width: 100%;
  max-width: 900px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  padding: 1rem 0 2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    max-width: 680px;
  }

  @media (max-width: 580px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    max-width: 380px;
  }
`;

const LogoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: pointer;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);

  animation: ${floatAnim} ${(props) => props.$duration}s ease-in-out infinite;
  animation-delay: ${(props) => props.$delay}s;

  transition:
    background 0.3s ease,
    border-color 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.09);
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  }
`;

const LogoImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  @media (max-width: 900px) {
    width: 60px;
    height: 60px;
  }

  @media (max-width: 580px) {
    width: 52px;
    height: 52px;
  }
`;

const LogoText = styled.p`
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
  line-height: 1.3;
  margin: 0;

  @media (max-width: 580px) {
    font-size: 0.75rem;
  }
`;

const GlowEffect = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  filter: blur(40px);
  pointer-events: none;

  ${(props) =>
    props.$pos === "top-left"
      ? `
    top: -200px;
    left: -200px;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%);
  `
      : `
    bottom: -200px;
    right: -200px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%);
  `}
`;
